import { useState, type ReactNode } from "react";
import type { CellHighlightApi } from "../lib/useCellHighlight";

/**
 * DetailViewShell — die VERERBBARE Detail-View-Basis (2026-07-13, Thomas: „wir
 * wollten ein abstraktes Detailview bauen das template-artig alles hat wie z.B.
 * 'Zurück zur Bearbeitung' … mach es VERERBBAR — dann haben alle Views die
 * wichtigsten Funktionalitäten geerbt"). In React heißt „vererbbar" Komposition,
 * nicht Klassen-Vererbung: EIN Shell-Component + EIN Highlight-Hook, den alle
 * Detail-Views einbinden, statt die Kopfzeile/Panels/Footer pro View zu kopieren.
 *
 * Das Shell liefert die gemeinsame Gerüst-Struktur:
 *   - Kopfzeile: „← Zurück", Titel + Badge, Quelle/Dateiname + Karten-ID-Chip,
 *     Status/Meta.
 *   - Hinweis-Slot (`notices`) und optionaler Human-Quality-Gate-Slot.
 *   - ZWEI-PANEL-Layout mit GLEICH HOHEN Panels (beide füllen dieselbe Höhe,
 *     jedes eigenständig scrollbar) — behebt Thomas' Excel-Beschwerde „Quelle
 *     und Berechnung sind unterschiedlich groß". Alternativ ein freier `children`-
 *     Body für die komplexen Sibit-Views (Split-Pane/Compare) — die erben so
 *     Kopfzeile + Footer, ohne ihr Editier-/Gate-Verhalten anzufassen.
 *   - Footer-Slot (`actions`) für die kind-agnostische CardDetailActions-Leiste,
 *     damit ALLE Views „↻ Zurück zur Bearbeitung" & Co. erben.
 *
 * Das gemeinsame DOM (`.ts-detail` / `.ts-detail-header` / `.ts-detail-meta`)
 * ist identisch zum bisherigen handkopierten Markup — die Migration der Live-
 * Views ist damit rein strukturell.
 */

/** Klick-zu-Kopieren Karten-ID — bisher 3× kopiert (Timesheet/Diäten/Excel/
 *  Zulagenblatt), jetzt EINE geteilte Quelle. */
export function CardIdChip({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — silent */ }
  };
  return (
    <>
      Karten-ID{" "}
      <code
        onClick={onCopy}
        title={copied ? "✓ kopiert" : `Klick zum Kopieren: ${id}`}
        style={{
          fontSize: 11, cursor: "pointer", userSelect: "all",
          padding: "1px 4px", borderRadius: 3,
          background: copied ? "#dcfce7" : "#f1f5f9",
          border: `1px solid ${copied ? "#86efac" : "#e2e8f0"}`,
        }}
      >
        {copied ? "✓ " : ""}{id}
      </code>
    </>
  );
}

export interface DetailViewShellProps {
  /** Modal-Modus (App.tsx-Overlay): kein „← Zurück" im Kopf (der Modal-Rahmen
   *  hat sein eigenes ✕/ESC). */
  modal?: boolean;
  onBack?: () => void;
  /** h2-Inhalt: Titel + Kind-Badge + ggf. read-only/dirty-Marker. */
  title: ReactNode;
  /** Inhalt der Quelle-Zeile (Link/Dateiname · Bytes · <CardIdChip/>). */
  source?: ReactNode;
  /** Inhalt der `.ts-detail-meta`-Zeile (Mitarbeiter-Select, Status, Toggles …). */
  meta?: ReactNode;
  /** Warn-/Fehlerblöcke unter der Kopfzeile (über dem Body). */
  notices?: ReactNode;
  /** Optionaler Human-Quality-Gate-Block (unter den Hinweisen, über dem Body). */
  qualityGate?: ReactNode;
  /** Zwei-Panel-Body: Quelle links, Daten rechts (gleich hoch, je scrollbar). */
  left?: ReactNode;
  right?: ReactNode;
  /** Highlight-Hook: dessen containerRef wird auf den Panel-Wrapper gehängt, damit
   *  Klick-zum-Springen über beide Panels hinweg findet. */
  highlight?: CellHighlightApi<any>;
  /** Freier Body statt des Zwei-Panel-Layouts (Sibit-Views). Wird nur genutzt,
   *  wenn weder `left` noch `right` gesetzt sind. */
  children?: ReactNode;
  /** Footer-Leiste (i.d.R. <CardDetailActions>…</CardDetailActions>). */
  actions?: ReactNode;
  className?: string;
}

export function DetailViewShell({
  modal,
  onBack,
  title,
  source,
  meta,
  notices,
  qualityGate,
  left,
  right,
  highlight,
  children,
  actions,
  className,
}: DetailViewShellProps) {
  const hasPanels = left !== undefined || right !== undefined;
  return (
    <div className={`ts-detail${className ? " " + className : ""}`}>
      <div className="ts-detail-header">
        {!modal && onBack && (
          <button type="button" className="link" onClick={onBack}>← Zurück</button>
        )}
        <h2>{title}</h2>
        {source && (
          <div className="ts-source-file" style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
            {source}
          </div>
        )}
        {meta && <div className="ts-detail-meta">{meta}</div>}
      </div>

      {notices}
      {qualityGate}

      {hasPanels ? (
        <div className="dv-panels" ref={highlight?.containerRef}>
          <div className="dv-panel dv-panel-left">{left}</div>
          <div className="dv-panel dv-panel-right">{right}</div>
        </div>
      ) : (
        children
      )}

      {actions}
    </div>
  );
}
